import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '../store/auth'
import { useUserStore } from '../store/user'
import { useOrgStore } from '../store/org'
import { supabase } from '@/lib/supabase'
import { queryClient } from '@/main'
import { canRetryNetworkTransportError } from '@/shared/utils/network-error-utils'
import { env } from '../config/env'
import {
  LOGIN_PATH,
  LOGIN_REASON_PARAM,
  authRefusalReasonOf,
} from '@/constants/login'
import { hardNavigate } from '@/shared/utils/route-utils/hard-navigate'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** The org this call acts on, sent as `x-organization-id` instead of the active one. */
    actAsOrganizationId?: string
  }
}
let isRefreshing = false
type QueuedRequest = {
  resolve: (value: AxiosResponse | PromiseLike<AxiosResponse>) => void
  reject: (reason?: AxiosError) => void
  originalRequest: AxiosRequestConfig
}
let failedQueue: QueuedRequest[] = []

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      // Retry the original request after a successful SDK refresh. Queued
      // requests get _retry too: their refresh already happened (the leader's),
      // so a second 401 must reject instead of starting another refresh cycle.
      ;(
        prom.originalRequest as AxiosRequestConfig & { _retry?: boolean }
      )._retry = true
      prom.resolve(connector(prom.originalRequest))
    }
  })

  failedQueue = []
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

let isSigningOut = false

const signOutTo = async (target: string) => {
  if (isSigningOut) return
  isSigningOut = true
  try {
    await supabase.auth.signOut().catch(() => {})
    useAuthStore.getState().reset()
    useUserStore.getState().reset()
    queryClient.clear()
    if (window.location.pathname !== LOGIN_PATH) {
      hardNavigate(target)
    }
  } finally {
    isSigningOut = false
  }
}

// Bearer-only transport: no cookies ride along. (POST /auth/logout opts back
// in per-request to let the API clear legacy pre-Bearer session cookies.)
const connector = axios.create({
  baseURL: env.VITE_API_URL,
  // Cap how long a request may hang. Without this, a request in flight when a
  // backend instance is torn down (Railway rollover) waits until the socket
  // resets (~40s), freezing the view. On timeout the query layer (TanStack
  // Query, default retry) refetches against the healthy instance. Uploads opt
  // out in the request interceptor — large multipart bodies run long by design.
  timeout: 30000,
})

connector.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean
      _networkRetry?: boolean
    }

    if (canRetryNetworkTransportError(error, originalRequest)) {
      originalRequest._networkRetry = true
      await wait(500)
      return connector(originalRequest)
    }

    const refusalReason = authRefusalReasonOf(error)
    if (error.response?.status === 401 && refusalReason) {
      // A refresh would mint a token the API refuses all the same.
      await signOutTo(
        `${LOGIN_PATH}?${new URLSearchParams({ [LOGIN_REASON_PARAM]: refusalReason })}`
      )
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If a refresh is already in progress, queue the request.
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve,
            reject,
            originalRequest,
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // The SDK is the ONE refresher (autoRefreshToken). Ask it to rotate the
        // session once; the request interceptor re-reads the fresh access token.
        const { data, error: refreshError } =
          await supabase.auth.refreshSession()
        if (refreshError || !data.session) {
          throw refreshError ?? new Error('Session refresh failed')
        }
        // After a successful refresh, retry any queued requests.
        processQueue(null)
        return connector(originalRequest)
      } catch (refreshError) {
        // Refresh failed — session is invalid. Clean up like logout.
        processQueue(refreshError as AxiosError)
        await signOutTo(LOGIN_PATH)
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

/**
 * Routes that run without an org context: unauthenticated entry points plus
 * logout. They must not get the x-organization-id header (nor the missing-org
 * warning). /auth/activate and /auth/deactivate are NOT here — they are
 * org-scoped admin actions behind MultiTenantGuard.
 */
const NO_ORG_HEADER_ROUTES = [
  '/auth/logout',
  '/auth/accept-invite',
  '/auth/forgot-password',
  '/auth/resend-link',
]

connector.interceptors.request.use(
  async (config) => {
    const url = config.url ?? ''
    const skipOrgHeader = NO_ORG_HEADER_ROUTES.some((route) =>
      url.includes(route)
    )

    const headers = new axios.AxiosHeaders(config.headers)

    // Uploads (multipart FormData) can legitimately exceed the default timeout
    // on slow uplinks — never abort a file mid-transfer.
    if (config.data instanceof FormData) {
      config.timeout = 0
    }

    // Attach the user's access token (SDK owns it in localStorage). The BE
    // guard verifies this Bearer JWT locally and feeds RLS from its claims.
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (session?.access_token) {
      headers.set('Authorization', `Bearer ${session.access_token}`)
    }

    // Inject org ID header on org-scoped routes, re-read on every pass so a
    // retried request follows an org switch — unless the call names its org
    // (`actAsOrganizationId`): SUPER_ADMIN acts on a picked org in « Contrat
    // d'une organisation » (#3049) without switching to it.
    if (!skipOrgHeader) {
      const orgId =
        config.actAsOrganizationId ?? useOrgStore.getState().organizationId
      if (orgId) {
        headers.set('x-organization-id', orgId)
      } else {
        console.warn(
          `[connector] Missing organizationId for request to ${url}.`
        )
      }
    }

    config.headers = headers
    return config
  },
  (error) => Promise.reject(error)
)

export default connector
